import { useEffect } from 'react'

/**
 * `IframeHandler` closes the Reapit Elements menu when the iframe gains focus.
 *
 * Behavior:
 * - It listens to `blur` events on the window. If the `activeElement` is an `<iframe>`,
 * - it dispatches a synthetic `Escape` keypress event to the open menu element,
 * - supporting both legacy (`.el-deprecated-menu-popover`) and
 * modern (`.el-popover.el-menu`) menu implementations.
 *
 * This approach is cleaner and leverages built-in accessibility and event handling.
 *
 * @returns {null} This component does not render any UI.
 *
 * @example
 * // Use inside a layout or top-level component where iframes might be present
 * <IframeHandler />
 */
export const IframeHandler = () => {
  // close the menu when iframe is focused
  useEffect(() => {
    const controller = new AbortController()

    const handleWindowBlur = () => {
      if (document.activeElement?.tagName === 'IFRAME') {
        const allMenus = [
          ...document.querySelectorAll('[data-open="true"].el-deprecated-menu-popover'),
          ...document.querySelectorAll('.el-popover.el-menu[role="menu"]'),
        ] as (HTMLElement & { hidePopover?: () => void })[]

        allMenus.forEach((menu) => {
          if (typeof menu.hidePopover === 'function') {
            menu.hidePopover()
          } else {
            // Fallback for older menu popovers
            const escEvent = new KeyboardEvent('keydown', {
              key: 'Escape',
              code: 'Escape',
              keyCode: 27,
              bubbles: true,
            })
            menu.dispatchEvent(escEvent)
          }
        })
      }
    }
    // Listen for blur events on the window
    // This will trigger when the iframe is focused
    window.addEventListener('blur', handleWindowBlur, {
      signal: controller.signal,
    })

    // This ensures the event listener is cleaned up when the component unmounts
    return () => {
      controller.abort()
    }
  }, [])
  return null
}
